import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { fetchConfig } from "../lib/api";
import Seo from "../components/Seo";
import Header from "../components/Header";
import Footer from "../components/Footer";

const chf = (n) => new Intl.NumberFormat("fr-CH", { maximumFractionDigits: 0 }).format(Math.round(n || 0)) + " CHF";

export default function Agences() {
  const [ag, setAg] = useState(null);
  const [sel, setSel] = useState(0);
  const [clients, setClients] = useState(5);
  const [revente, setRevente] = useState("");

  useEffect(() => {
    fetchConfig().then((c) => {
      const a = c.agences || { remise_pct: 30, plans: [] };
      setAg(a);
    }).catch(() => setAg({ remise_pct: 30, plans: [{ nom: "Essentiel", prix: 197 }, { nom: "Croissance", prix: 497 }, { nom: "Suite", prix: 797 }] }));
  }, []);

  const remise = Number(ag?.remise_pct) || 0;
  const plans = ag?.plans || [];
  const cout = (prix) => Math.round(prix * (1 - remise / 100));

  const plan = plans[sel] || { nom: "", prix: 0 };
  const reventeVal = revente === "" ? plan.prix : Number(revente) || 0;
  const coutRevendeur = cout(plan.prix);
  const calc = useMemo(() => {
    const margeU = Math.max(0, reventeVal - coutRevendeur);
    return { margeU, margeMois: margeU * (Number(clients) || 0), margeAn: margeU * (Number(clients) || 0) * 12 };
  }, [reventeVal, coutRevendeur, clients]);

  return (
    <div>
      <Seo routeKey="/pilote" path="/agences" title="Agences & revendeurs — marge revendeur | VIRALYA" description="Revendez VIRALYA à vos clients avec une marge claire. Prix affichés, remise revendeur et calculateur de marge mensuelle et annuelle." />
      <Header />
      <main className="page">
        <div className="container">
          <Link to="/" className="back-link"><ArrowLeft size={15} /> Accueil</Link>
          <span className="eyebrow"><span className="sq" />Agences & revendeurs</span>
          <h1 className="h2" style={{ marginBottom: "0.5rem" }}>Revendez VIRALYA avec une marge claire</h1>
          <p className="lead" style={{ marginBottom: "2rem", maxWidth: "60ch" }}>Des prix affichés, pas de « sur devis ». Vous achetez au prix revendeur ({remise}% de remise) et vous fixez votre prix de revente. Voici la marge que vous dégagez.</p>

          {!ag ? <p className="muted">Chargement…</p> : (
            <div className="ag-grid">
              <div className="glass card" data-testid="agences-prices">
                <h3 className="h3" style={{ marginBottom: "1rem", fontSize: "1.1rem" }}>Prix & marge par plan</h3>
                <div style={{ overflowX: "auto" }}>
                  <table className="data-table">
                    <thead><tr><th>Plan</th><th>Prix public</th><th>Prix revendeur</th><th>Votre marge/mois</th></tr></thead>
                    <tbody>
                      {plans.map((p, i) => (
                        <tr key={p.nom + i} data-testid={`agences-plan-${i}`}>
                          <td><strong>{p.nom}</strong></td>
                          <td className="tnum">{chf(p.prix)}</td>
                          <td className="tnum">{chf(cout(p.prix))}</td>
                          <td className="tnum" style={{ color: "var(--v-accent-text)", fontWeight: 600 }}>{chf(p.prix - cout(p.prix))}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="muted" style={{ fontSize: "0.8rem", marginTop: "0.75rem" }}>Marge indiquée si vous revendez au prix public. Vous restez libre de votre prix de revente.</p>
              </div>

              <div className="glass card" data-testid="agences-calculator">
                <h3 className="h3" style={{ marginBottom: "1rem", fontSize: "1.1rem" }}>Calculateur de marge</h3>
                <div className="field" style={{ marginBottom: "1rem" }}>
                  <label>Plan revendu</label>
                  <select value={sel} onChange={(e) => { setSel(Number(e.target.value)); setRevente(""); }} data-testid="agences-plan-select">
                    {plans.map((p, i) => <option key={i} value={i}>{p.nom} — {chf(p.prix)}</option>)}
                  </select>
                </div>
                <div className="field" style={{ marginBottom: "1rem" }}>
                  <label>Votre prix de revente (CHF/mois)</label>
                  <input type="number" min="0" step="10" value={revente} placeholder={String(plan.prix)} onChange={(e) => setRevente(e.target.value)} data-testid="agences-revente" />
                </div>
                <div className="field" style={{ marginBottom: "1.25rem" }}>
                  <label>Nombre de clients</label>
                  <input type="number" min="0" step="1" value={clients} onChange={(e) => setClients(e.target.value)} data-testid="agences-clients" />
                </div>

                <div className="ag-results">
                  <div className="ag-res"><div className="ag-res-l">Coût revendeur</div><div className="ag-res-v tnum">{chf(coutRevendeur)}<span className="ag-res-u">/client/mois</span></div></div>
                  <div className="ag-res"><div className="ag-res-l">Marge unitaire</div><div className="ag-res-v tnum">{chf(calc.margeU)}<span className="ag-res-u">/client/mois</span></div></div>
                  <div className="ag-res ag-res--hl"><div className="ag-res-l">Marge mensuelle</div><div className="ag-res-v tnum" data-testid="agences-marge-mois">{chf(calc.margeMois)}</div></div>
                  <div className="ag-res ag-res--hl"><div className="ag-res-l">Marge annuelle</div><div className="ag-res-v tnum" data-testid="agences-marge-an">{chf(calc.margeAn)}</div></div>
                </div>

                <Link to="/audit" className="btn btn--primary" style={{ marginTop: "1.5rem" }} data-testid="agences-audit-cta">Réserver un audit gratuit <ArrowRight size={15} /></Link>
              </div>
            </div>
          )}
          <div className="glass card" style={{ marginTop: "1.5rem" }} data-testid="agences-ecosystem">
            <p style={{ margin: 0 }}>La chaîne est complète : Viralya attire et chauffe l'audience ; dès qu'un lead est prêt à être converti, il bascule dans <a href="https://app.loomy-crm.com" target="_blank" rel="noopener" className="inline-link">Loomy CRM</a> pour la vente, et les relances client directes passent par <a href="https://talk-flow.ai" target="_blank" rel="noopener" className="inline-link">Talk-Flow</a>.</p>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
